/* eslint-disable @typescript-eslint/camelcase */
import { Divider, Grid, Typography, makeStyles } from '@material-ui/core';
import i18n from 'i18n';
import {
  EnvironmentFields,
  PeriodCountAccess,
  DateFilterFields,
} from 'modules/environments/interfaces';
import React from 'react';
import { connect } from 'react-redux';
import AccountBalanceWalletIcon from '@material-ui/icons/AccountBalanceWallet';
import DvrIcon from '@material-ui/icons/Dvr';
import BarChartIcon from '@material-ui/icons/BarChart';
import ApplicationState from 'store/interfaces';
import { PermissionsData, RolesEnum } from 'modules/authenticate/interfaces';
import Permission from 'components/ValidPermission';
import FilterSelector from 'components/FilterSelector';
import { filterPeriodOptions } from 'utils/graphs';
import {
  setPeriodFilter,
  setPerformanceDateFilter,
} from 'modules/environments/actions';
import { useParams } from 'react-router';
import clsx from 'clsx';
import DateFilter from 'components/DateFilter';
import EnvironmentsTemplate from '../Templates/Environments';
import styles from './styles';
import CPUGraphCard from './CPUGraphCard';
import MemoryGraphCard from './MemoryGraphCard';
import EnvironmentCostGraphCard from '../Admin/Costs/EnvironmentCostGraphCard';
import FlavorsCost from '../Admin/Costs/FlavorsCost';
import SessionsHistoryGraphCardPerformance from '../EnvironmentDashboard/SessionsHistoryGraphCardPerformance';
import OperationalTimeGraphCard from './OperationalTimeGraphCard';

const useStyles = makeStyles(styles);

export const requiredPerformancePermissions: Array<keyof PermissionsData> = [
  'list_environment_cost',
  'list_dashboard_performance',
  'list_environment_dashboard',
  'can_edit_environment_monitoring_settings',
];

interface Props {
  environmentSelected: EnvironmentFields | null;
  role?: RolesEnum;
  performanceDateFilter: DateFilterFields;
  periodFilter: PeriodCountAccess;
  permissions?: PermissionsData;
  setPeriodFilter: (prop: string) => void;
  setPerformanceDateFilter: (prop: DateFilterFields) => void;
}

const PerformanceEnvironment = ({
  environmentSelected,
  role,
  performanceDateFilter,
  periodFilter,
  permissions,
  setPeriodFilter,
  setPerformanceDateFilter,
}: Props) => {
  const classes = useStyles();
  const { section } = useParams<{ section: string }>();
  const [openDateFilter, setOpenDateFilter] = React.useState(false);

  return (
    <EnvironmentsTemplate
      currentPage="performance"
      titlePage={environmentSelected?.name}
      breadcrumbs={[
        {
          name: i18n.t('ENVIRONMENTS_KEY'),
          link: '/environments',
        },
        {
          name: i18n.t('PERFORMANCE_PAGE.PERFORMANCE_KEY'),
        },
      ]}
      titleContent={i18n.t('PERFORMANCE_PAGE.PERFORMANCE_KEY')}
      subtitleContent={i18n.t('PERFORMANCE_PAGE.PERFORMANCE_TITLE_KEY')}
      actionsContent={
        <Permission value={requiredPerformancePermissions}>
          <Grid
            container
            justify="flex-end"
            alignContent="center"
            className={classes.filters}
          >
            <Grid item>
              <Typography className={classes.filterTitle}>
                {`${i18n.t('PERFORMANCE_PAGE.FILTERS_KEY')}:`}
              </Typography>
            </Grid>
            <Grid className={classes.filtersContainer}>
              <DateFilter
                onOpenFilter={setOpenDateFilter}
                onSubmit={setPerformanceDateFilter}
              />
              {!openDateFilter && (
                <>
                  <Divider
                    variant="middle"
                    orientation="vertical"
                    className={classes.dividerFilters}
                  />
                  <FilterSelector
                    options={filterPeriodOptions(setPeriodFilter)}
                    currentValue={{
                      label: i18n.t(
                        `ENVIRONMENT_DASHBOARD.LAST_${periodFilter.toUpperCase()}`
                      ),
                      onClick: () => null,
                    }}
                  />
                </>
              )}
            </Grid>
          </Grid>
        </Permission>
      }
    >
      <Permission value={requiredPerformancePermissions}>
        <Divider className={classes.dividerTop} />
        {role === 'superadmin' && permissions?.list_environment_cost && (
          <>
            <Grid
              container
              className={clsx({
                [classes.costsSection]: section === 'costs',
              })}
            >
              <Grid container alignItems="center">
                <AccountBalanceWalletIcon className={classes.iconDividers} />
                <Typography className={classes.titleDividers}>
                  {`${i18n.t('PERFORMANCE_PAGE.COSTS_DETAILS_KEY')}:`}
                </Typography>
              </Grid>

              <Grid container spacing={2}>
                <FlavorsCost dateFilter={performanceDateFilter} />
                <EnvironmentCostGraphCard dateFilter={performanceDateFilter} />
              </Grid>
            </Grid>
            <Divider className={classes.divider} />
          </>
        )}
        <Permission value="list_dashboard_performance">
          <Grid container alignItems="center">
            <DvrIcon className={classes.iconDividers} />
            <Typography className={classes.titleDividers}>
              {`${i18n.t('PERFORMANCE_PAGE.CPU_MEMORY_USE_KEY')}:`}
            </Typography>
          </Grid>
          <Grid container spacing={2}>
            <CPUGraphCard dateFilter={performanceDateFilter} />
            <MemoryGraphCard dateFilter={performanceDateFilter} />
          </Grid>
          <Divider className={classes.divider} />
        </Permission>

        <Grid container alignItems="center">
          <BarChartIcon className={classes.iconDividers} />
          <Typography className={classes.titleDividers}>
            {`${i18n.t(
              'PERFORMANCE_PAGE.SESSIONS_VACANCIES_OPERATIONAL_KEY'
            )}:`}
          </Typography>
        </Grid>
        <Grid container spacing={2}>
          <Permission value="list_environment_dashboard">
            <SessionsHistoryGraphCardPerformance
              dateFilter={performanceDateFilter}
            />
          </Permission>
          <Permission value="can_edit_environment_monitoring_settings">
            <OperationalTimeGraphCard dateFilter={performanceDateFilter} />
          </Permission>
        </Grid>
      </Permission>
    </EnvironmentsTemplate>
  );
};

const mapStateToProps = ({
  environments: { environmentSelected, performanceDateFilter, periodFilter },
  authenticate: { data },
}: ApplicationState) => ({
  environmentSelected,
  performanceDateFilter,
  periodFilter,
  role: data?.role,
  permissions: data?.permissions,
});

export default connect(mapStateToProps, {
  setPeriodFilter,
  setPerformanceDateFilter,
})(PerformanceEnvironment);
